import React, { useState } from 'react';
import parse from 'html-react-parser';
import { TitlePage } from '../../../components/TitlePage/TitlePage';
import ImageViewer from '../../../components/ImageViewer/ImageViewer';
import './ChatPost.scss';

/**
 * Компонент для отображения поста в чате
 * @param {Object} props
 * @param {Object} props.post - Данные поста
 * @param {string} props.post.title - Заголовок поста
 * @param {string} props.post.text - Текст поста
 * @param {string} props.post.date - Дата поста
 * @param {Array} props.post.media - Медиа файлы
 * @param {Object} props.post.account - Информация об аккаунте
 * @param {string} props.post.account.icon_link - Иконка аккаунта
 * @param {boolean} props.loading - Состояние загрузки
 * @param {string} props.placeholderText - Текст-заглушка
 */

// ─── SmallMediaGrid ──────────────────────────────────────────────────────────

const SmallMediaGrid = ({ media, setIsOpen, setSelectedImageIndex }) => {
    if (!media || media.length === 0) return null;

    const getFileType = (url) => {
        const ext = url.split('.').pop()?.toLowerCase();
        if (['mp4', 'webm', 'ogg', 'mov'].includes(ext)) return 'video';
        return 'image';
    };

    // Показываем только первые 4 медиа
    const displayMedia = media.slice(0, 4);
    const remainingCount = media.length - 4;

    const renderMedia = (item, index) => {
        const type = getFileType(item.link);

        if (type === 'video') {
            return (
                <div
                    key={index}
                    onClick={() => {
                        setSelectedImageIndex(index)
                        setIsOpen(true)
                    }}
                    className="small-media-grid__item small-media-grid__item--video"
                >
                    <video
                        className="small-media-grid__video"
                        muted
                        loop
                        playsInline
                    >
                        <source src={item.link} />
                    </video>
                </div>
            );
        }

        return (
            <div
                key={index}
                onClick={() => {
                    setSelectedImageIndex(index)
                    setIsOpen(true)
                }}
                className="small-media-grid__item"
            >
                <img
                    className="small-media-grid__image"
                    src={item.link}
                    alt={`Медиа ${index + 1}`}
                    loading="lazy"
                />
            </div>
        );
    };

    return (
        <div className={`small-media-grid small-media-grid--${Math.min(displayMedia.length, 4)}`}>
            {displayMedia.map(renderMedia)}
            {remainingCount > 0 && (
                <div className="small-media-grid__item small-media-grid__item--more">
                    <span className="small-media-grid__more-count">+{remainingCount}</span>
                </div>
            )}
        </div>
    );
};

const ChatPost = ({
    post = {},
    loading = false,
    placeholderText = 'Выберите пост, чтобы просмотреть его данные'
}) => {

    const hasContent = post?.date && post?.title;
    const [isOpen, setIsOpen] = useState(false)
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)

    return (
        <>
            <ImageViewer
                isOpen={isOpen}
                images={post?.media?.map(({ link }) => link)}
                initialIndex={selectedImageIndex}
                onClose={(e) => setIsOpen(false)}
            />

            <div className="chat-post">
                <div className="chat-post__header">
                    <TitlePage
                        medium
                        title={post?.title || 'Пост не выбран'}
                    />
                    {post?.date && (
                        <div className="chat-post__header-meta">
                            <span className="chat-post__date">
                                {new Date(post.date).toLocaleDateString('ru-RU')}
                            </span>
                            {post.account?.icon_link && (
                                <img
                                    className="chat-post__account-icon"
                                    src={post.account.icon_link}
                                    alt="Аккаунт"
                                />
                            )}
                        </div>
                    )}
                </div>

                <div className="chat-post__body">
                    {loading ?
                        <div className="chat-post__loader">
                            <div className="chat-post__skeleton chat-post__skeleton--title" />
                            <div className="chat-post__skeleton chat-post__skeleton--text" />
                            <div className="chat-post__skeleton chat-post__skeleton--text" />
                            <div className="chat-post__skeleton chat-post__skeleton--media" />
                        </div>
                        : <>
                            <div className="chat-post__content">
                                <p className="chat-post__text">
                                    {parse(post.text || '')}
                                </p>
                            </div>
                            {post.media?.length > 0 && (
                                <div className="chat-post__media">
                                    <SmallMediaGrid
                                        setIsOpen={setIsOpen}
                                        setSelectedImageIndex={setSelectedImageIndex}
                                        media={post.media} />
                                </div>
                            )}
                        </>
                    }
                </div>
            </div>
        </>
    );
};

export default ChatPost;